import React, { useState, useEffect } from "react";
import axios from "axios";
import Table from "../../core/pagination/datatable";

const InventoryLedger = () => {
    const [ledgers, setLedgers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");

    useEffect(() => {
        fetchLedgers();
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories/active");
            setCategories(res.data.filter(c => c.type !== "Menu"));
        } catch (error) {
            console.error(error);
        }
    };

    const fetchLedgers = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/inventory/ledger");
            setLedgers(res.data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const columns = [
        { title: "Timestamp", dataIndex: "createdAt", key: "createdAt", render: (t) => <div className="text-secondary small fw-medium"><i className="fas fa-calendar-alt me-1"></i>{new Date(t).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</div> },
        { title: "Asset / SKU", key: "variantProduct", render: (_, record) => (
            <div>
               <div className="fw-bold text-dark">{record.variant?.product?.name || "Deleted Product"}</div>
               <div className="text-muted small"><i className="fas fa-box pe-1"></i>{record.variant?.name || "Unknown SKU"}</div>
            </div>
        ) },
        { 
            title: "Transaction Activity", dataIndex: "type", key: "type",
            render: (t) => (
                <div className="d-flex align-items-center">
                    <span className={`d-flex justify-content-center align-items-center rounded-circle me-2 shadow-sm ${t==='IN' ? 'bg-success bg-gradient text-white' : t==='OUT' ? 'bg-warning bg-gradient text-dark' : 'bg-info text-white'}`} style={{ width: '32px', height: '32px' }}>
                        <i className={`fas ${t==='IN'?'fa-arrow-down':t==='OUT'?'fa-arrow-up':'fa-exchange-alt'}`}></i>
                    </span>
                    <span className="fw-bold text-secondary">{t === 'IN' ? 'Asset Added (IN)' : t === 'OUT' ? 'Dispatched (OUT)' : t}</span>
                </div>
            )
        },
        { 
            title: "Volume & Impact", key: "qtycost",
            render: (_, r) => (
                <div>
                   <div className={`fw-bold fs-6 ${r.type==='IN' ? 'text-success' : r.type==='OUT' ? 'text-warning' : 'text-info'}`}>
                       {r.type==='IN'?'+':'-'} {r.quantity} {r.variant?.unit}
                   </div>
                   {r.unitCost > 0 && <div className="text-secondary small" style={{ fontSize: '11px' }}>@ Rs {r.unitCost} / unit</div>}
                </div>
            )
        },
        { title: "Net Locked Value", key: "netValue", render: (_, r) => <div className="fw-bold text-dark fs-6">Rs {(r.quantity * (r.unitCost || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}</div> },
        { title: "Notes / Ref", dataIndex: "notes", key: "notes", render: (t) => <div className="small text-muted bg-light border p-2 rounded-3" style={{maxWidth: '220px', minHeight: '32px'}}>{t || <span className="opacity-50 fst-italic">No reference code</span>}</div> },
    ];

    // Filter the extracted table data
    const filteredLedgers = ledgers.filter(record => {
        const matchesCategory = selectedCategory === "All" || record.variant?.product?.category === selectedCategory;
        const searchLower = searchQuery.toLowerCase();
        
        const matchesSearch = (
            record.variant?.product?.name?.toLowerCase().includes(searchLower) ||
            record.variant?.name?.toLowerCase().includes(searchLower) ||
            record.type?.toLowerCase().includes(searchLower) ||
            record.notes?.toLowerCase().includes(searchLower)
        );

        return matchesCategory && matchesSearch;
    });

    return (
        <div className="page-wrapper bg-light bg-opacity-50">
            <div className="content">
                <div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                    <div>
                        <h4 className="fw-bold mb-1">Inventory Transactions Ledger</h4>
                        <p className="text-muted mb-0">Permanent, timestamped record of every stock movement</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-body py-3 d-flex flex-column flex-md-row flex-wrap gap-3 align-items-stretch align-items-md-center">
                        <div className="input-group" style={{ flex: '1 1 300px' }}>
                            <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                            <input 
                                type="text" 
                                className="form-control border-start-0 ps-0" 
                                placeholder="Search records..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div style={{ flex: '1 1 200px' }}>
                            <select 
                                className="form-select" 
                                value={selectedCategory} 
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="All">All Categories</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>{c.type} - {c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="card shadow-sm border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                        ) : (
                            <div className="table-responsive">
                                <Table columns={columns} dataSource={filteredLedgers.map(l => ({ ...l, key: l._id }))} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default InventoryLedger;
